import { useState } from 'react';
import { Scale } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Dispute, useDisputesQuery, useResolveDisputeMutation } from '@/client/api/payment/finance';
import { formatMoney } from '@/client/types/payment';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { RelationshipLink } from '@/client/components/admin/Payment/RelationshipLink';
import { EmptyRow, ErrorNote, FinancePage, card, fmtDate, selectCls, td, th } from './shell';

const STATUSES = ['awaiting-merchant-feedback', 'awaiting-bank-feedback', 'pending', 'resolved'];
const label = (s?: string | null) => (s ? s.replace(/-/g, ' ') : '—');

type Action = { dispute: Dispute; resolution: 'merchant-accepted' | 'declined' };

const DisputesPage = () => {
  const refund = useHasPermission('payment.refund');
  const canResolve = refund.isSuperAdmin || refund.hasPermission;
  const [status, setStatus] = useState('');
  const { data, isLoading, error } = useDisputesQuery(status ? { status } : {});
  const mutation = useResolveDisputeMutation();
  const [action, setAction] = useState<Action | null>(null);
  const [message, setMessage] = useState('');
  const [amount, setAmount] = useState('');

  const open = (dispute: Dispute, resolution: Action['resolution']) => {
    setAction({ dispute, resolution });
    setMessage('');
    setAmount(String((dispute.transaction?.amount ?? 0) / 100));
  };

  const submit = async () => {
    if (!action) return;
    if (!message.trim()) {
      toast.error('A message is required', { richColors: true });
      return;
    }
    try {
      await mutation.mutateAsync({
        id: action.dispute.id,
        resolution: action.resolution,
        message: message.trim(),
        ...(action.resolution === 'merchant-accepted' ? { refund_amount: Number(amount) } : {}),
      });
      toast.success('Dispute updated at Paystack', { richColors: true });
      setAction(null);
    } catch (e: any) {
      toast.error(e?.message || 'Paystack did not accept the resolution', { richColors: true });
    }
  };

  return (
    <FinancePage
      icon={Scale}
      title="Disputes"
      description={`Chargebacks and disputes, read live from the ${data?.mode ?? ''} Paystack account (it follows the Live / Test switch). Accepting a dispute refunds the customer; declining contests it with the bank.`}
      actions={
        <select
          aria-label="Dispute status"
          className={selectCls}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {label(s)}
            </option>
          ))}
        </select>
      }
    >
      <ErrorNote error={error} />
      <div className={card}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr className="bg-gray-50">
                {['Dispute', 'Customer', 'Amount', 'Our payment', 'Status', 'Respond by', ''].map((h, i) => (
                  <th key={i} className={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {isLoading && <EmptyRow cols={7}>Loading disputes from Paystack…</EmptyRow>}
              {!isLoading && !error && !data?.disputes?.length && (
                <EmptyRow cols={7}>No disputes</EmptyRow>
              )}
              {data?.disputes?.map((d) => {
                const open_ = d.status !== 'resolved';
                return (
                  <tr key={d.id}>
                    <td className={td}>
                      <span className="font-mono">{d.id}</span>
                      <span className="block text-xs capitalize text-gray-500">{d.category}</span>
                    </td>
                    <td className={td}>{d.customer?.email || '—'}</td>
                    <td className={`${td} font-semibold tabular-nums`}>
                      {formatMoney((d.transaction?.amount ?? 0) / 100, d.transaction?.currency || d.currency)}
                      <span className="block font-mono text-xs font-normal text-gray-500">
                        {d.transaction?.reference}
                      </span>
                    </td>
                    <td className={td}>
                      {d.payment ? (
                        <RelationshipLink kind="payment" id={d.payment.pid} />
                      ) : (
                        <span className="text-amber-700">Not recorded</span>
                      )}
                    </td>
                    <td className={`${td} capitalize`}>
                      {label(d.status)}
                      {d.resolution && (
                        <span className="block text-xs text-gray-500">{label(d.resolution)}</span>
                      )}
                    </td>
                    <td className={`${td} text-gray-500`}>{fmtDate(open_ ? d.dueAt : d.resolvedAt)}</td>
                    <td className={td}>
                      {canResolve && open_ && (
                        <div className="flex justify-end gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => open(d, 'declined')}>
                            Contest
                          </Button>
                          <Button type="button" size="sm" onClick={() => open(d, 'merchant-accepted')}>
                            Accept and refund
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent>
          {action && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {action.resolution === 'merchant-accepted' ? 'Accept and refund' : 'Contest'} dispute{' '}
                  {action.dispute.id}
                </DialogTitle>
                <DialogDescription>
                  {action.resolution === 'merchant-accepted'
                    ? 'Paystack refunds the customer from your balance. This cannot be undone.'
                    : 'Paystack passes your response to the customer’s bank, which makes the final decision.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {action.resolution === 'merchant-accepted' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="dispute-amount">
                      Refund amount ({action.dispute.transaction?.currency || 'GHS'})
                    </Label>
                    <Input
                      id="dispute-amount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="dispute-message">Message to Paystack</Label>
                  <Input
                    id="dispute-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="What happened, in one or two sentences"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAction(null)}>
                  Cancel
                </Button>
                <Button type="button" disabled={mutation.isPending} onClick={submit}>
                  {action.resolution === 'merchant-accepted' ? 'Refund customer' : 'Send response'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </FinancePage>
  );
};

export default DisputesPage;
